import { defineStore } from "pinia";
import {
  computed,
  ComputedRef,
  reactive,
  Reactive,
  Ref,
  ref,
  watch
} from "vue";
import axios from "axios";
import { io } from "socket.io-client";
import * as Sentry from "@sentry/vue";

import elementInputs from "@/data/generated/inputLocation.json";
import { useCalculationStore } from "./calculationStore";
import { useAuthStore } from "./authStore";
import { useSpaceStore } from "./spaceStore";
import { useRouter } from "vue-router";
import { useAxiosStore } from "./axiosStore";
import { useSocketStore } from "./socketStore";
import { module, organization, space, tooltip } from "@/types/global";

export const useMainStore = defineStore("mainStore", () => {
  const spaceStore = useSpaceStore();
  const authStore = useAuthStore();
  const socketStore = useSocketStore();
  const calculationStore = useCalculationStore();
  const router = useRouter();

  const modules: Ref<module[]> = ref([]);
  const moduleValues: Ref<Record<string, number>> = computed(() => {
    return modules.value.reduce((result, module) => {
      result[module.name] = module.value;
      return result;
    }, {} as Record<string, number>);
  });

  const tooltips: Ref<Record<string, tooltip>> = ref({});

  const copyData: Reactive<copyData> = reactive({
    allElements: [],
    locations: [],
    id: "",
    name: "",
    spaceId: "",
    variantName: "",
    extra: undefined
  });

  const activeSpaceId = ref<string | null>(null);

  const occupiedFields = ref<Record<string, Record<string, string>>>({});
  const activeUsers = ref<Record<string, string>>({});
  const activeElements = computed(() => {
    return Object.entries(activeUsers.value).reduce(
      (result, [userId, elementId]) => {
        result[elementId] = userId;
        return result;
      },
      {} as Record<string, string>
    );
  });

  const calculationSpaces: Ref<space[]> = ref([]);
  const organizations: Ref<organization[]> = ref([]);
  const collaborationUsers = computed(() => {
    const result = {};

    for (const space of calculationSpaces.value) {
      for (const user of space.users) {
        result[user.id] = user;
        result[user.id].initials = user.first_name[0] + user.last_name[0];
      }
    }
    return result;
  });

  function getInitials(id: string) {
    if (collaborationUsers.value[id]) {
      return collaborationUsers.value[id].initials;
    }
  }

  const dragItem = ref(null);

  async function loadInitialData() {
    await Promise.all([
      loadCalculationSpaces(),
      loadOrganizations(),
      loadModules(),
      loadMachineDB()
    ]);

    axios.get("/api/tooltips")
      .then((response) => {
        tooltips.value = response.data.reduce((acc, tooltip) => {
          acc[tooltip.id] = tooltip;
          try {
            acc[tooltip.id].legend = JSON.parse(tooltip.legend);
          } catch (e) {
            acc[tooltip.id].legend = {};
          }
          return acc;
        }, {} as Record<string, tooltip>);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  async function loadCalculationSpaces() {
    return axios
      .get("/api/calculation-spaces")
      .then((response) => {
        calculationSpaces.value = response.data;
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  async function loadOrganizations() {
    return axios
      .get("/api/organization/list")
      .then((response) => {
        organizations.value = response.data;
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  async function loadModules() {
    return axios
      .get("/api/modules")
      .then((response) => {
        modules.value = response.data.sort((a, b) => a.value - b.value);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  function createSpace(name: string, description: string) {
    axios
      .post("/api/calculation-spaces", {
        name: name,
        description: description
      })
      .then((response) => {
        calculationSpaces.value.push(response.data);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  function addCollaborator(email: string, space: number) {
    axios
      .post("/api/calculation-spaces/" + space + "/add-user", {
        email: email
      })
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error(error);
      });
  }

  async function openSpace(spaceId: string) {
    if (calculationSpaces.value.find((space) => space.id === spaceId)) {
      if (activeSpaceId.value !== null) {
        // Leave current space
        socketStore.socket.emit("leave-space", activeSpaceId.value);
        spaceStore.clearData();
      }

      activeSpaceId.value = spaceId;
      console.log("Opening space:", spaceId);
      socketStore.socket.emit("enter-space", spaceId, (activeData) => {
        console.log("Active data:", activeData);
        for (const data of activeData) {
          if (data.focusElement) {
            activeUsers.value[data.user] = data.focusElement;

            if (data.focusField) {
              occupiedFields[data.focusElement] = {
                ...occupiedFields[data.focusElement],
                [data.focusField]: data.user
              };

              calculationStore.focusElementSocket(
                data.focusElement,
                data.focusField,
                data.user
              );
            }
          }
        }
      });
      authStore.globalLoading = true;
      return spaceStore.loadAllElements().then(() => {
        authStore.globalLoading = false;
      });
    } else {
      console.log("Invalid space ID");
      leaveSpace();
      Promise.reject();
    }
  }

  async function leaveSpace() {
    await router.replace({ path: "/" });
    await spaceStore.checkIfEverythingIsCorrectlyStored();
    socketStore.socket.emit("leave-space", activeSpaceId.value);

    if (calculationStore.calculating) {
      await new Promise((resolve) => {
        watch(
          () => calculationStore.calculating,
          () => {
            resolve(null);
          }
        );
      });
    }

    console.log("User left space, clearing data");

    activeSpaceId.value = null;
    spaceStore.clearData();
  }

  async function exitSpace() {
    await spaceStore.checkIfEverythingIsCorrectlyStored();
    socketStore.socket.emit("leave-space", activeSpaceId.value);

    if (calculationStore.calculating) {
      await new Promise((resolve) => {
        watch(
          () => calculationStore.calculating,
          () => {
            resolve(null);
          }
        );
      });
    }

    console.log("User left space, clearing data");

    activeSpaceId.value = null;
    spaceStore.clearData();
  }

  async function acceptInvitation(spaceId: string) {
    axios
      .post(`/api/calculation-spaces/${spaceId}/accept-invitation`, { spaceId })
      .then(() => {
        for (const space of calculationSpaces.value) {
          if (space.id === spaceId) {
            space.state = 1;
            break;
          }
        }
      });
  }

  async function declineInvitation(spaceId: string) {
    axios
      .post(`/api/calculation-spaces/${spaceId}/decline-invitation`, {
        spaceId
      })
      .then(() => {
        calculationSpaces.value = calculationSpaces.value.filter(
          (space) => space.id !== spaceId
        );
      });
  }

  async function acceptOrganizationInvitation(
    organizationId: number,
    callback: () => void
  ) {
    return axios
      .post(`/api/organization/accept/` + organizationId, {
        organizationId: organizationId
      })
      .then(() => {
        loadCalculationSpaces();
        callback();
      });
  }

  async function declineOrganizationInvitation(organizationId: number) {
    return axios.post(`/api/organization/decline`, {
      organizationId: organizationId
    });
  }

  async function removeSpace(spaceId: string) {
    await axios.post(`/api/calculation-spaces/${spaceId}/leave`);
    calculationSpaces.value = calculationSpaces.value.filter(
      (space) => space.id !== spaceId
    );
  }

  async function leaveOrganization(organizationId: number) {
    return axios.post(`/api/organization/leave/${organizationId}`).then(() => {
      loadOrganizations();
      loadCalculationSpaces();

    });
  }

  function organizationRights(organizationId: string) {
    const orga = organizations.value.find(
      (organization) =>
        Number(organization.organization.id) === Number(organizationId)
    );

    return orga?.rights;
  }

  function organizationDelete(organizationId: number) {
    return axios.delete(`/api/organization/${organizationId}`).then(() => {
      loadOrganizations();
      loadCalculationSpaces();
      router.push({ path: "/profile" });
    });
  }


  const machines: Ref<any[]> = ref([]);

  const machineDb: ComputedRef<Record<string, any>> = computed(() => {
    return machines.value.reduce((result, machine) => {
      result[machine.id] = machine;
      return result
    }, {})
  });

  async function loadMachineDB() {
    machines.value = await axios
      .get("/api/database/machine/list")
      .then((res) => res.data);
  }

  function deleteMachine(machineId: string) {
    axios.delete(`/api/database/machine/${machineId}`)
      .then(() => {
        machines.value = machines.value.filter((machine) => machine.id !== machineId);
      })
      .catch((error) => {
        alert(error.response.data.message);
      });
  }

  function hasModule(moduleName: string) {
    console.log("hasModule", moduleName, spaceStore.currentSpace?.licence, moduleValues.value[moduleName])
    return spaceStore.currentSpace?.licence && spaceStore.currentSpace?.licence & moduleValues.value[moduleName];
  }

  return {
    machines,
    loadMachineDB,
    deleteMachine,
    machineDb,
    dragItem,
    loadCalculationSpaces,
    loadOrganizations,
    loadInitialData,
    loadModules,
    modules,
    organizations,
    calculationSpaces,
    createSpace,
    addCollaborator,
    occupiedFields,
    activeElements,
    collaborationUsers,
    activeSpaceId,
    copyData,
    openSpace,
    leaveSpace,
    activeUsers,
    getInitials,
    acceptInvitation,
    declineInvitation,
    removeSpace,
    acceptOrganizationInvitation,
    declineOrganizationInvitation,
    organizationRights,
    exitSpace,
    organizationDelete,
    tooltips,
    hasModule,
    moduleValues,
    leaveOrganization
  };
});
